import type { SessionSnapshot } from "./types";

const SESSION_KEY = "refinement-canvas.auth.session";
const SESSION_CHANGED_EVENT_NAME = "refinement-canvas:auth-session-changed";

// localStorage への書き込みは即時 storage イベントを生成しないため、
// 同一タブ内で saveSessionSnapshot / clearSessionSnapshot をトリガーした側からも
// 購読者へ通知できるようカスタムイベントを併用する。
function notifySessionChanged(): void {
  window.dispatchEvent(new Event(SESSION_CHANGED_EVENT_NAME));
}

/**
 * SessionSnapshot を localStorage から読み出す。
 *
 * 取得できない場合や JSON が壊れている場合は undefined を返す。
 */
export function loadSessionSnapshot(): SessionSnapshot | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  const raw = window.localStorage.getItem(SESSION_KEY);
  if (raw === null) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(raw) as Partial<SessionSnapshot> | null;
    if (
      parsed === null ||
      typeof parsed !== "object" ||
      typeof parsed.userId !== "string" ||
      typeof parsed.name !== "string" ||
      (parsed.source !== "meiden-connect" && parsed.source !== "mock")
    ) {
      return undefined;
    }
    return {
      userId: parsed.userId,
      name: parsed.name,
      email: typeof parsed.email === "string" ? parsed.email : undefined,
      affiliation:
        typeof parsed.affiliation === "string" ? parsed.affiliation : undefined,
      avatarImageUrl:
        typeof parsed.avatarImageUrl === "string" ? parsed.avatarImageUrl : undefined,
      source: parsed.source,
      globalPermissionIds: Array.isArray(parsed.globalPermissionIds)
        ? parsed.globalPermissionIds.filter(
            (value): value is string => typeof value === "string"
          )
        : undefined,
    };
  } catch {
    return undefined;
  }
}

/**
 * SessionSnapshot を localStorage に保存し、購読者へ通知する。
 *
 * 画面起動時にローカルストレージから即時に読み出せるよう、idp-file: 形式の
 * アバター URL や Blob URL は除外して保存する。
 */
export function saveSessionSnapshot(snapshot: SessionSnapshot): void {
  if (typeof window === "undefined") {
    return;
  }

  // Blob URL / idp-file: / 巨大な data URL は再起動後の即時利用ができないため、
  // 永続化対象から除外する。data URL もここで弾いて、ヘッダー送信用の小さい URL は
  // ランタイム側で再生成させる。
  const persistableAvatarImageUrl =
    typeof snapshot.avatarImageUrl === "string" &&
    !snapshot.avatarImageUrl.startsWith("blob:") &&
    !snapshot.avatarImageUrl.startsWith("idp-file:") &&
    !snapshot.avatarImageUrl.startsWith("data:")
      ? snapshot.avatarImageUrl
      : undefined;

  const persistable: SessionSnapshot = {
    ...snapshot,
    avatarImageUrl: persistableAvatarImageUrl,
  };

  window.localStorage.setItem(SESSION_KEY, JSON.stringify(persistable));
  notifySessionChanged();
}

/**
 * SessionSnapshot を localStorage から削除し、購読者へ通知する。
 */
export function clearSessionSnapshot(): void {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.removeItem(SESSION_KEY);
  notifySessionChanged();
}

/**
 * 別タブでの localStorage 更新および同一タブでの save/clear を購読する。
 *
 * @param listener セッション情報が変化したときに呼ばれるコールバック
 * @returns 購読解除関数
 */
export function subscribeSessionSnapshot(listener: () => void): () => void {
  if (typeof window === "undefined") {
    return () => undefined;
  }

  const handleStorage = (event: StorageEvent): void => {
    if (event.key === SESSION_KEY || event.key === null) {
      listener();
    }
  };

  window.addEventListener("storage", handleStorage);
  window.addEventListener(SESSION_CHANGED_EVENT_NAME, listener);

  return () => {
    window.removeEventListener("storage", handleStorage);
    window.removeEventListener(SESSION_CHANGED_EVENT_NAME, listener);
  };
}
